/**
 * intentClassifier — AI-first intent classification with regex fallback.
 *
 * Pipeline:
 *   1. checkGreetingOrHelp  → free, instant  → greeting/help response
 *   2. classifyIntent (AI)  → ~300 tokens, <2s → LLM via n8n wf.classify_intent
 *   3. detectIntent (regex) → free, instant  → fallback if AI fails or returns nothing
 *
 * The AI layer fires first for best accuracy on messy, ambiguous, or non-English input.
 * Regex is the safety net when AI is unavailable or returns no result.
 */

import { detectIntent, checkGreetingOrHelp, getIntentDef, getFollowUpQuestion } from './chatEngine';
import { N8N_BASE_URL, N8N_PROXY_BASE, N8N_WEBHOOK_PREFIX } from '../shared/constants';

// ── Configuration ───────────────────────────────────────────────────────────

const CLASSIFY_ENDPOINT = N8N_WEBHOOK_PREFIX + 'classify-intent';

/**
 * Confidence threshold: regex results at or above this skip the AI layer.
 * 'high' from detectIntent means score >= 8 (multi-word keyword match).
 */
const CONFIDENCE_THRESHOLD = 'high';

/**
 * Timeout for the AI classify call (ms). Short because it's a small prompt.
 */
const CLASSIFY_TIMEOUT_MS = 10000;

// ── Types ───────────────────────────────────────────────────────────────────

/**
 * @typedef {Object} ClassifyResult
 * @property {'greeting'|'help'|'intent'|'fallback'|'error'} type
 * @property {string|null} greetingResponse - If greeting/help, the canned response
 * @property {string[]} intents            - Detected intents
 * @property {string|null} primaryIntent   - Top intent
 * @property {Object} params               - Extracted params (targetLanguage, replyTone, etc.)
 * @property {string[]} missingParams      - Params still needed
 * @property {Object|null} followUpQuestion - Follow-up to ask if params missing
 * @property {string} confidence           - 'high' | 'low' | 'none'
 * @property {string} source               - 'regex' | 'ai' | 'greeting'
 * @property {boolean} topicSwitch
 * @property {number} aiTokens             - Tokens used by AI call (0 if regex-only)
 */

// ── Main API ────────────────────────────────────────────────────────────────

/**
 * Classify user intent with AI-first, regex-fallback.
 *
 * @param {string} message - User's chat message
 * @param {Object} [opts]
 * @param {Object} [opts.conversationContext] - Pending intent / collected params
 * @param {Object} [opts.emailContext]        - { subject, bodyText } for AI context
 * @param {string} [opts.n8nBaseUrl]          - Override for n8n base URL
 * @param {AbortSignal} [opts.signal]         - Abort signal for cancellation
 * @returns {Promise<ClassifyResult>}
 */
export async function classifyUserIntent(message, opts = {}) {
  const {
    conversationContext = {},
    emailContext = {},
    n8nBaseUrl = N8N_PROXY_BASE,
    signal,
  } = opts;

  // ── Layer 0: Empty input ──
  console.group('[intentClassifier] classifyUserIntent:', message);
  const trimmed = (message || '').trim();
  if (!trimmed) {
    console.log('[intentClassifier] → Layer 0: empty input');
    console.groupEnd();
    return {
      type: 'fallback',
      greetingResponse: null,
      intents: [],
      primaryIntent: null,
      params: {},
      missingParams: [],
      followUpQuestion: null,
      confidence: 'none',
      source: 'regex',
      topicSwitch: false,
      aiTokens: 0,
    };
  }

  // ── Layer 1: Greeting / Help (free, instant) ──
  const greeting = checkGreetingOrHelp(trimmed);
  if (greeting.isGreeting || greeting.isHelp) {
    console.log('[intentClassifier] → Layer 1:', greeting.isGreeting ? 'greeting' : 'help');
    console.groupEnd();
    return {
      type: greeting.isGreeting ? 'greeting' : 'help',
      greetingResponse: greeting.response,
      intents: [],
      primaryIntent: null,
      params: {},
      missingParams: [],
      followUpQuestion: null,
      confidence: 'high',
      source: 'greeting',
      topicSwitch: false,
      aiTokens: 0,
    };
  }

  // ── Layer 2: AI classification (primary) ──
  // Try AI first for best accuracy on ambiguous, messy, or non-English input
  console.log('[intentClassifier] → Layer 2: calling AI classify endpoint (primary)');
  try {
    const aiResult = await callClassifyEndpoint({
      userMessage: trimmed,
      emailSubject: emailContext.subject || '',
      emailText: emailContext.bodyText || '',
      n8nBaseUrl,
      signal,
    });

    console.log('[intentClassifier] AI result:', { intents: aiResult.intents, confidence: aiResult.confidence, reasoning: aiResult.reasoning, tokens: aiResult.tokens });

    if (aiResult.intents.length > 0) {
      // AI found intents — also run regex to extract params it may miss
      console.log('[intentClassifier] → Layer 2: AI found intents, using AI result');
      console.groupEnd();

      // Run regex silently to extract structured params (language, tone, style, etc.)
      const regexDetection = detectIntent(trimmed, conversationContext);
      const mergedParams = { ...regexDetection.params };

      // AI may return targetLanguage, replyTone, etc. — these override regex params
      if (aiResult.params) {
        Object.assign(mergedParams, aiResult.params);
      }

      // Use AI intents but check for missing required params via regex definitions
      const primaryIntent = aiResult.intents[0];
      const def = getIntentDef(primaryIntent);
      const missingParams = [];
      if (def) {
        for (const p of def.requiredParams) {
          if (!mergedParams[p] && !conversationContext[p]) missingParams.push(p);
        }
      }

      let followUpQuestion = null;
      if (missingParams.length > 0) {
        followUpQuestion = getFollowUpQuestion(missingParams[0]);
      }

      return {
        type: 'intent',
        greetingResponse: null,
        intents: aiResult.intents,
        primaryIntent,
        params: mergedParams,
        missingParams,
        followUpQuestion,
        confidence: aiResult.confidence >= 0.7 ? 'high' : 'low',
        source: 'ai',
        topicSwitch: false,
        aiTokens: aiResult.tokens || 0,
      };
    }

    // AI returned nothing — fall through to regex
    console.log('[intentClassifier] → Layer 2: AI found nothing, falling through to regex');
  } catch (err) {
    // AI call failed — fall through to regex
    console.warn('[intentClassifier] → Layer 2: AI classify failed, falling through to regex:', err.message);
  }

  // ── Layer 3: Regex detection (fallback) ──
  // AI returned nothing or failed — try regex as safety net
  const detection = detectIntent(trimmed, conversationContext);

  console.log('[intentClassifier] → Layer 3 regex fallback result:', { intents: detection.intents, confidence: detection.confidence, params: detection.params });

  if (detection.intents.length > 0) {
    console.log('[intentClassifier] → Layer 3: regex found intents');
    console.groupEnd();
    return {
      type: 'intent',
      greetingResponse: null,
      intents: detection.intents,
      primaryIntent: detection.primaryIntent,
      params: detection.params,
      missingParams: detection.missingParams,
      followUpQuestion: detection.followUpQuestion,
      confidence: detection.confidence,
      source: 'regex',
      topicSwitch: detection.topicSwitch,
      aiTokens: 0,
    };
  }

  // Both AI and regex found nothing — genuine fallback
  console.warn('[intentClassifier] → Layer 3: regex also found nothing, genuine fallback');
  console.groupEnd();
  return {
    type: 'fallback',
    greetingResponse: null,
    intents: [],
    primaryIntent: null,
    params: {},
    missingParams: [],
    followUpQuestion: null,
    confidence: 'none',
    source: 'none',
    topicSwitch: false,
    aiTokens: 0,
  };
}

// ── HTTP call to n8n classify-intent webhook ────────────────────────────────

/**
 * POST to the classify-intent sub-workflow webhook.
 *
 * @param {Object} params
 * @param {string} params.userMessage
 * @param {string} params.emailSubject
 * @param {string} params.emailText
 * @param {string} params.n8nBaseUrl
 * @param {AbortSignal} [params.signal]
 * @returns {Promise<{intents: string[], confidence: number, reasoning: string, tokens: number, params?: Object}>}
 */
async function callClassifyEndpoint({ userMessage, emailSubject, emailText, n8nBaseUrl, signal }) {
  const url = n8nBaseUrl + CLASSIFY_ENDPOINT;
  console.log('[intentClassifier:AI] POST', url);
  console.log('[intentClassifier:AI] payload:', { userMessage, emailSubject, emailTextLength: (emailText || '').length });

  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    console.warn('[intentClassifier:AI] timeout after', CLASSIFY_TIMEOUT_MS, 'ms');
    controller.abort();
  }, CLASSIFY_TIMEOUT_MS);

  const combinedSignal = signal
    ? combineAbortSignals(signal, controller.signal)
    : controller.signal;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userMessage,
        emailSubject,
        emailText: (emailText || '').substring(0, 500),
      }),
      signal: combinedSignal,
    });

    clearTimeout(timeoutId);
    console.log('[intentClassifier:AI] response:', response.status, response.statusText);

    if (!response.ok) {
      const errText = await response.text().catch(() => '');
      console.error('[intentClassifier:AI] error body:', errText.substring(0, 300));
      throw new Error(`Classify endpoint returned ${response.status}`);
    }

    const data = await response.json();
    console.log('[intentClassifier:AI] response data:', data);

    return {
      intents: Array.isArray(data.intents) ? data.intents.filter(isValidIntent) : [],
      confidence: typeof data.confidence === 'number' ? data.confidence : 0.5,
      reasoning: data.reasoning || '',
      tokens: data.tokens || 0,
      params: data.params || null,
    };
  } catch (err) {
    clearTimeout(timeoutId);
    console.error('[intentClassifier:AI] fetch failed:', err.name, err.message);
    throw err;
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const VALID_INTENTS = new Set(['summarize', 'actions', 'reply', 'translate', 'rephrase', 'compose']);

function isValidIntent(intent) {
  return VALID_INTENTS.has(intent);
}

/**
 * Combine two AbortSignals — aborts if either fires.
 */
function combineAbortSignals(signal1, signal2) {
  // If AbortSignal.any is available (modern browsers), use it
  if (typeof AbortSignal.any === 'function') {
    return AbortSignal.any([signal1, signal2]);
  }
  // Fallback: create a new controller and wire both
  const combined = new AbortController();
  const onAbort = () => combined.abort();
  signal1.addEventListener('abort', onAbort, { once: true });
  signal2.addEventListener('abort', onAbort, { once: true });
  return combined.signal;
}

// ── Exports for testing ─────────────────────────────────────────────────────

export const __internal__ = {
  CLASSIFY_ENDPOINT,
  CONFIDENCE_THRESHOLD,
  CLASSIFY_TIMEOUT_MS,
  VALID_INTENTS,
  isValidIntent,
  callClassifyEndpoint,
};
